import axios from 'axios';
import {
  ApiResponse,
  CompanyData,
  CompanyHistory,
  CompanyBeneficiaries,
  AnnualAccountsData,
  LegalActsData
} from '../types/inpi';

const API_URL = import.meta.env.VITE_API_BASE_URL;

// Helper function to get auth headers
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

// Helper function to handle API errors
const handleApiError = (error: any) => {
  console.error('INPI API Error:', error);
  
  // Check if it's a network error (ECONNREFUSED, etc.)
  if (error.message?.includes('Network Error') || 
      error.code === 'ECONNABORTED' || 
      error.code === 'ECONNREFUSED' ||
      error.response?.data?.error?.includes('connect')) {
    throw {
      response: {
        data: {
          message: 'Erreur de connexion au serveur INPI. Veuillez vérifier votre connexion réseau et les paramètres du serveur.',
          error: 'Network Error'
        }
      }
    };
  }
  
  // Check if it's an authentication error from the backend
  if (error.response?.data?.error === 'Failed to authenticate with INPI API' ||
      error.response?.status === 401 || 
      error.response?.status === 403) {
    throw {
      response: {
        data: {
          message: 'Erreur d\'authentification avec l\'API INPI. Veuillez vérifier vos identifiants.',
          error: 'Failed to authenticate with INPI API'
        }
      }
    };
  }
  
  throw error;
};

// Formalités API services
export const getCompanyBySiren = async (siren: string): Promise<CompanyData> => {
  try {
    const response = await axios.get<ApiResponse<CompanyData>>(
      `${API_URL}/inpi/companies/${siren}`,
      { headers: getAuthHeaders() }
    );
    return response.data.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getCompanyHistory = async (siren: string, page = 1, perPage = 10): Promise<CompanyHistory> => {
  try {
    const response = await axios.get<ApiResponse<CompanyHistory>>(
      `${API_URL}/inpi/companies/${siren}/history`,
      {
        params: { page, perPage },
        headers: getAuthHeaders(),
      }
    );
    return response.data.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getCompanyBeneficiaries = async (siren: string): Promise<CompanyBeneficiaries> => {
  try {
    const response = await axios.get<ApiResponse<CompanyBeneficiaries>>(
      `${API_URL}/inpi/companies/${siren}/beneficiaries`,
      { headers: getAuthHeaders() }
    );
    return response.data.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Comptes Annuels API services
export const getAnnualAccounts = async (
  siren: string,
  params: {
    page?: number;
    per_page?: number;
    date_depot_debut?: string;
    date_depot_fin?: string;
    date_cloture_debut?: string;
    date_cloture_fin?: string;
    confidentialite?: string;
  } = {}
): Promise<AnnualAccountsData> => {
  try {
    const response = await axios.get<ApiResponse<AnnualAccountsData>>(
      `${API_URL}/inpi/companies/${siren}/annual-accounts`,
      {
        params,
        headers: getAuthHeaders(),
      }
    );
    return response.data.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getAnnualAccountDetails = async (accountId: string): Promise<any> => {
  try {
    const response = await axios.get<ApiResponse<any>>(
      `${API_URL}/inpi/annual-accounts/${accountId}`,
      { headers: getAuthHeaders() }
    );
    return response.data.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Actes API services
export const getLegalActs = async (
  siren: string,
  params: {
    page?: number;
    per_page?: number;
    date_depot_debut?: string;
    date_depot_fin?: string;
    type_acte?: string;
  } = {}
): Promise<LegalActsData> => {
  try {
    const response = await axios.get<ApiResponse<LegalActsData>>(
      `${API_URL}/inpi/companies/${siren}/legal-acts`,
      {
        params,
        headers: getAuthHeaders(),
      }
    );
    return response.data.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getLegalActDetails = async (actId: string): Promise<any> => {
  try {
    const response = await axios.get<ApiResponse<any>>(
      `${API_URL}/inpi/legal-acts/${actId}`,
      { headers: getAuthHeaders() }
    );
    return response.data.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Document download service
export const getDocumentDownloadUrl = (documentId: string, documentType?: 'acte' | 'compte_annuel'): string => {
  const typeParam = documentType ? `&type=${documentType}` : '';
  return `${API_URL}/inpi/documents/${documentId}/download?token=${localStorage.getItem('token')}${typeParam}`;
};

const inpiService = {
  getCompanyBySiren,
  getCompanyHistory,
  getCompanyBeneficiaries,
  getAnnualAccounts,
  getAnnualAccountDetails,
  getLegalActs,
  getLegalActDetails,
  getDocumentDownloadUrl,
};

export default inpiService; 